import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { movies } from "@/mocks/movies";
import RatingBadge from "@/components/base/RatingBadge";

const HERO_SLIDES = [
  {
    movieId: "changye-jiangming",
    bg: "https://readdy.ai/api/search-image?query=Vast%20cinematic%20sci-fi%20vista%20of%20a%20glowing%20eclipse%20over%20a%20futuristic%20city%20skyline%20at%20night%2C%20deep%20navy%20blue%20and%20warm%20orange%20color%20grading%2C%20dramatic%20dark%20sky%2C%20atmospheric%20fog%2C%20stylized%20concept%20art%2C%20wide%20panoramic%20composition&width=1600&height=900&seq=hero-01&orientation=landscape",
    tag: "热门推荐",
  },
  {
    movieId: "xingji-huixiang",
    bg: "https://readdy.ai/api/search-image?query=Vast%20cinematic%20space%20vista%20with%20a%20giant%20swirling%20galaxy%20and%20a%20distant%20starship%2C%20deep%20navy%20blue%20and%20warm%20orange%20tones%2C%20dramatic%20dark%20background%2C%20stylized%20sci-fi%20concept%20art%2C%20wide%20panoramic%20composition&width=1600&height=900&seq=hero-02&orientation=landscape",
    tag: "院线大片",
  },
  {
    movieId: "quanyou-xinjiyuan",
    bg: "https://readdy.ai/api/search-image?query=Epic%20cinematic%20fantasy%20landscape%20with%20a%20castle%20on%20a%20cliff%20above%20stormy%20seas%20and%20distant%20dragons%2C%20deep%20navy%20blue%20stormy%20sky%20with%20warm%20torchlight%2C%20stylized%20concept%20art%2C%20wide%20panoramic%20composition%2C%20dark%20dramatic%20tone&width=1600&height=900&seq=hero-03&orientation=landscape",
    tag: "现象级剧集",
  },
];

const slides = HERO_SLIDES.map((slide) => ({
  ...slide,
  movie: movies.find((m) => m.id === slide.movieId),
})).filter((slide) => Boolean(slide.movie));

export default function HeroCarousel() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, []);

  const goTo = (index: number) => {
    setActive((index + slides.length) % slides.length);
  };

  return (
    <section className="relative w-full h-[380px] md:h-[520px] overflow-hidden bg-primary-950">
      {slides.map((slide, index) => {
        const movie = slide.movie!;
        return (
          <div
            key={slide.movieId}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <img
              src={slide.bg}
              alt={`${movie.title} 焦点海报`}
              title={`${movie.title} - 影视影院焦点推荐`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent" />

            <div className="absolute inset-0">
              <div className="w-full max-w-[1280px] mx-auto h-full px-4 md:px-6 flex items-center">
                <div className="max-w-xl">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-500 text-white text-xs font-label mb-3">
                    <i className="ri-fire-line" />
                    {slide.tag}
                  </span>
                  <h2 className="font-heading font-extrabold text-3xl md:text-5xl text-white leading-tight">
                    {movie.title}
                  </h2>
                  <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-background-200">
                    <RatingBadge rating={movie.rating} size="md" />
                    <span>{movie.genres.join(" / ")}</span>
                    <span className="text-background-300">{movie.region}</span>
                    <span className="text-background-300">{movie.releaseDate}</span>
                  </div>
                  <p className="mt-3 text-sm md:text-base text-background-200 leading-relaxed line-clamp-2 md:line-clamp-3">
                    {movie.summary}
                  </p>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/detail/${movie.id}`}
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-label font-medium transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-line text-lg" />
                      查看详情
                    </Link>
                    <Link
                      to="/category?sort=hot"
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300/60 text-white text-sm font-label font-medium hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-list-check-2" />
                      更多热门
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Controls */}
      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(active - 1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-foreground-950/40 hover:bg-foreground-950/70 text-white transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(active + 1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-foreground-950/40 hover:bg-foreground-950/70 text-white transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {slides.map((slide, index) => (
          <button
            key={slide.movieId}
            type="button"
            aria-label={`切换到第 ${index + 1} 张`}
            onClick={() => goTo(index)}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              index === active ? "w-7 bg-accent-500" : "w-2 bg-white/60 hover:bg-white"
            }`}
          />
        ))}
      </div>
    </section>
  );
}